import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import XLSyncLogo from "@/components/brand/XLSyncLogo";

const LAST_UPDATED = "October 19, 2026";
const CONTACT_EMAIL = "privacy@xlsync.app";

const sections = [
  {
    id: "overview",
    title: "1. Overview",
    body: (
      <>
        <p>
          XLSync (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) provides a clinical imaging
          intelligence platform for hospitals, core labs, distributors, and related healthcare
          organizations. This Privacy Policy explains how we collect, use, store, share, and
          protect information when you use the XLSync web application and related services
          (the &quot;Service&quot;).
        </p>
        <p>
          The Service is invite-only and intended for authorized professional users. It may
          process protected health information (PHI) and other sensitive clinical data on behalf
          of customer organizations. Where we process PHI for a customer, we act as a service
          provider / data processor under that organization&apos;s instructions.
        </p>
      </>
    ),
  },
  {
    id: "information-we-collect",
    title: "2. Information We Collect",
    body: (
      <>
        <p className="font-medium text-[var(--xl-text)]">Account and user information</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Name, email address, and hashed password</li>
          <li>Role, organization affiliation, hospital, territory, and reporting relationships</li>
          <li>Authentication tokens stored in your browser (access and refresh tokens)</li>
        </ul>

        <p className="font-medium text-[var(--xl-text)] pt-2">Patient and clinical data</p>
        <p>
          When authorized users upload or enter records, the Service may store identifiers and
          clinical details such as:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Patient identifiers (e.g. UHID / external UHID), name, age, gender, date of birth</li>
          <li>Contact details (phone, email), blood group, referring doctor, institution</li>
          <li>
            Imaging studies and files (including DICOM and related modalities), reports, and
            extracted measurements
          </li>
          <li>
            Study metadata (modality, status, anatomy types, valve manufacturer/type/size, tags,
            discussion comments)
          </li>
        </ul>

        <p className="font-medium text-[var(--xl-text)] pt-2">Sharing and transfer data</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Share-link and transfer metadata (recipients, permissions, expiry, download counts)</li>
          <li>Sender and recipient email addresses used for secure file transfers and notifications</li>
        </ul>

        <p className="font-medium text-[var(--xl-text)] pt-2">Operational and audit data</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Audit log entries for security-relevant actions (sign-in, logout, access, changes)</li>
          <li>Technical logs needed to operate, secure, and troubleshoot the Service</li>
        </ul>
      </>
    ),
  },
  {
    id: "how-we-use",
    title: "3. How We Use Information",
    body: (
      <>
        <p>We use information to:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Authenticate users and enforce role-based access controls</li>
          <li>Store, display, search, and manage patients, studies, images, and reports</li>
          <li>Support clinical workflows (upload, review, reporting, billing dashboards, SLA tracking)</li>
          <li>Enable authorized sharing and time-limited download links</li>
          <li>Provide optional PHI anonymization and restoration for authorized users</li>
          <li>Send transactional emails (password resets, share/transfer notifications, report alerts)</li>
          <li>Maintain audit trails and investigate security or misuse concerns</li>
          <li>Improve reliability, performance, and support of the Service</li>
        </ul>
        <p>
          We do not sell personal or patient information. We do not use clinical content for
          advertising.
        </p>
      </>
    ),
  },
  {
    id: "anonymization",
    title: "4. Anonymization",
    body: (
      <>
        <p>
          Authorized users with patient-management permissions may anonymize patient records.
          Anonymization clears identifiable PHI fields from the working record while retaining a
          private copy so that un-anonymization can restore those fields when permitted by your
          organization&apos;s policies.
        </p>
        <p>
          DICOM anonymization tools may also clear selected tags from imaging files when used by
          authorized users. Anonymization does not remove all operational metadata (for example,
          audit history or sharing records) unless your organization requests additional deletion.
        </p>
      </>
    ),
  },
  {
    id: "sharing",
    title: "5. How Information Is Shared",
    body: (
      <>
        <p>Information may be shared only as needed to operate the Service:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <span className="font-medium text-[var(--xl-text)]">Within your organization:</span>{" "}
            according to each user&apos;s role and data scope (for example, hospital, territory, or
            uploader-only visibility)
          </li>
          <li>
            <span className="font-medium text-[var(--xl-text)]">Authorized collaborators:</span>{" "}
            when a user with share permission grants access to another XLSync user or creates a
            share/transfer link
          </li>
          <li>
            <span className="font-medium text-[var(--xl-text)]">Service providers:</span>{" "}
            infrastructure and email delivery providers that process data under contract (for
            example, transactional email via Resend)
          </li>
          <li>
            <span className="font-medium text-[var(--xl-text)]">Legal requirements:</span>{" "}
            when disclosure is required by law, regulation, or valid legal process
          </li>
        </ul>
        <p>
          Public share and transfer links can be accessed without an XLSync account by anyone who
          has the link until the link expires or is deactivated. Recipients should treat links as
          confidential.
        </p>
      </>
    ),
  },
  {
    id: "security",
    title: "6. Security",
    body: (
      <>
        <p>We apply administrative, technical, and organizational measures designed to protect data, including:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Invite-only accounts and password-based authentication with hashed credentials</li>
          <li>Bearer access/refresh tokens with limited lifetimes</li>
          <li>Role-based permissions and server-side data scoping</li>
          <li>Audit logging of security-relevant events</li>
          <li>Rate limiting on certain public download endpoints</li>
        </ul>
        <p>
          No method of transmission or storage is completely secure. You are responsible for
          protecting your credentials, devices, and any share links you create.
        </p>
      </>
    ),
  },
  {
    id: "retention",
    title: "7. Data Retention",
    body: (
      <>
        <p>
          We retain account, clinical, sharing, and audit data for as long as needed to provide the
          Service to your organization, comply with legal obligations, resolve disputes, and enforce
          agreements. Customer organizations may request deletion or export of data subject to
          applicable law and contractual terms. Password-reset tokens are short-lived and expire
          automatically.
        </p>
      </>
    ),
  },
  {
    id: "cookies",
    title: "8. Cookies and Local Storage",
    body: (
      <>
        <p>
          XLSync uses browser local storage to keep you signed in (access token, refresh token, and
          cached user profile). We do not use third-party advertising cookies. Essential session
          and preference storage is required for the Service to function.
        </p>
      </>
    ),
  },
  {
    id: "your-rights",
    title: "9. Your Rights and Choices",
    body: (
      <>
        <p>Depending on your role and applicable law, you may be able to:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Access or update your account profile through your administrator</li>
          <li>Change your password or request a password reset</li>
          <li>Ask your organization to correct, anonymize, or delete patient records you are authorized to manage</li>
          <li>Revoke active share links or patient shares you control</li>
          <li>Sign out to clear local authentication state on your device</li>
        </ul>
        <p>
          Patients and data subjects who are not XLSync users should contact the healthcare
          organization that collected their information. We support customer organizations in
          responding to lawful requests.
        </p>
      </>
    ),
  },
  {
    id: "children",
    title: "10. Children",
    body: (
      <>
        <p>
          The Service is not directed to children and is not intended for personal consumer use by
          minors. Clinical records involving minors may be processed only when entered by authorized
          healthcare professionals under their organization&apos;s policies and applicable law.
        </p>
      </>
    ),
  },
  {
    id: "international",
    title: "11. International Transfers",
    body: (
      <>
        <p>
          Depending on your deployment and hosting configuration, information may be processed in
          regions where we or our service providers operate. Customer organizations are responsible
          for ensuring that their use of XLSync complies with cross-border transfer requirements
          applicable to them.
        </p>
      </>
    ),
  },
  {
    id: "changes",
    title: "12. Changes to This Policy",
    body: (
      <>
        <p>
          We may update this Privacy Policy from time to time. The &quot;Last updated&quot; date at
          the top of this page will reflect the latest revision. Continued use of the Service after
          changes become effective constitutes acceptance of the updated policy, except where
          additional consent is required by law or contract.
        </p>
      </>
    ),
  },
 
];

export default function PrivacyPolicy() {
  return (
    <div
      data-testid="privacy-policy-page"
      className="min-h-screen bg-[var(--xl-surf)] text-[var(--xl-text)]"
    >
      <header
        className="sticky top-0 z-10 border-b border-[var(--xl-line)] bg-[var(--xl-surf)]/95 backdrop-blur-sm"
        data-testid="privacy-policy-brand"
      >
        <div className="mx-auto flex w-full max-w-5xl items-center justify-between gap-4 px-4 py-3 sm:px-8 lg:px-10">
          <Link to="/login" aria-label="XLSync home" className="shrink-0">
            <XLSyncLogo size="sm" />
          </Link>
          <Link
            to="/login"
            data-testid="privacy-back-login"
            className="inline-flex items-center gap-1.5 text-sm text-[var(--xl-mut)] hover:text-[var(--xl-text)]"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to sign in
          </Link>
        </div>
      </header>

      <main className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-8 sm:py-12 lg:px-10">
        <div className="mb-8 sm:mb-10">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[var(--xl-text)]">
            Privacy Policy
          </h1>
          <p className="mt-2 text-sm text-[var(--xl-mut)]">
            Last updated: {LAST_UPDATED}
          </p>
        </div>

        <div className="space-y-8 sm:space-y-10 text-sm sm:text-[15px] leading-relaxed text-[var(--xl-mut)]">
          {sections.map((section) => (
            <section
              key={section.id}
              id={section.id}
              aria-labelledby={`${section.id}-heading`}
              className="space-y-3 scroll-mt-20"
            >
              <h2
                id={`${section.id}-heading`}
                className="text-base sm:text-lg font-semibold text-[var(--xl-text)]"
              >
                {section.title}
              </h2>
              <div className="space-y-3">{section.body}</div>
            </section>
          ))}
        </div>
      </main>

      <footer className="border-t border-[var(--xl-line)]">
        <p className="mx-auto max-w-5xl px-4 py-6 text-xs text-[var(--xl-mut2)] sm:px-8 lg:px-10">
          XLSync · Clinical imaging intelligence · Admin-invite only
        </p>
      </footer>
    </div>
  );
}
